import { App } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { getJwtExpiration, hasFileAccessScope } from '../../utils/jwt';
import { useAuth } from '../useAuth';

const fileService = new FileService();

/** sessionStorage: accessToken was already exchanged (or claimed) this tab. */
function btokenUsedStorageKey(accessToken: string): string {
  return `fg.btoken.used.${accessToken}`;
}

/** In-flight btoken promises shared across StrictMode remounts (same tab). */
const btokenInFlight = new Map<string, Promise<string>>();

/**
 * Custom React Hook for handling protected file access via a special token.
 *
 * This hook manages:
 * - Fetching a viewer token for a protected file **exactly once** per accessToken
 * - Storing the received tokens in the zustand store
 * - Handling navigation to the viewer route with proper query params
 * - Managing loading and error states and showing notifications on error
 *
 * Usage:
 * Call this hook in a component rendered inside a route with :fileUid and :token params.
 * It performs all side-effects automatically on mount (via useEffect).
 *
 * Returned values:
 * - isLoading: boolean indicating if the async flow is in progress
 * - error: error message string, or null if no error
 * - t: i18n translation function (can be used by consumer for UI)
 */
function useProtectedFileToken() {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const { getUserInfo } = useAuth();
  const { fileUid } = useParams<{ fileUid: string }>();
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Guard remounts within the same React tree (in addition to sessionStorage).
  const startedRef = useRef(false);

  const getViewer = useCallback(async () => {
    if (!fileUid || !token) {
      notification.error({
        title: t('errors.getViewerData'),
      });
      return;
    }

    const usedKey = btokenUsedStorageKey(token);

    // Reload / second mount after a prior exchange: never call btoken again.
    if (sessionStorage.getItem(usedKey) === 'done') {
      const message = t('errors.protectedLinkAlreadyUsed');
      setError(message);
      notification.error({ title: message });
      return;
    }

    setIsLoading(true);

    try {
      let responsePromise = btokenInFlight.get(token);
      if (!responsePromise) {
        // Claim before the network call so StrictMode / remount cannot start a second GET.
        sessionStorage.setItem(usedKey, 'pending');
        responsePromise = fileService.getFileToken(token).finally(() => {
          btokenInFlight.delete(token);
        });
        btokenInFlight.set(token, responsePromise);
      }

      const response = await responsePromise;
      sessionStorage.setItem(usedKey, 'done');

      if (response) {
        const tokenExpiry = getJwtExpiration(response);
        // File-scoped sessions have no refresh token; never keep a stale rToken.
        useAppStore.getState().setCustomTokens({
          token: response,
          rToken: hasFileAccessScope(response)
            ? ''
            : useAppStore.getState().customTokens?.rToken || '',
          tokenExpiry: tokenExpiry || undefined,
          rTokenExpiry: hasFileAccessScope(response)
            ? undefined
            : useAppStore.getState().customTokens?.rTokenExpiry,
        });
        useAppStore.getState().setToken(response);
      }

      await getUserInfo(false);

      // Preserve acToken on the viewer URL (anticapture / jwt actoken).
      navigate(`/viewer/${fileUid}?acToken=${encodeURIComponent(token)}`);
    } catch (e) {
      sessionStorage.setItem(usedKey, 'done');
      const status = (e as { status?: number })?.status;
      const message =
        status === 400 ? t('errors.protectedLinkAlreadyUsedOrExpired') : t('errors.getViewerData');
      setError(message);
      notification.error({
        title: message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [fileUid, token, t, getUserInfo, navigate, notification]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void getViewer();
  }, [getViewer]);

  return {
    isLoading,
    error,
    t,
  };
}

export default useProtectedFileToken;
